import bbox from "@turf/bbox";
import { featureCollection, point } from "@turf/helpers";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { BBox } from "geojson";
import {
  BillboardCollection,
  Cartesian2,
  Cartesian3,
  DistanceDisplayCondition,
  Entity,
  Math as CesiumMath,
  Matrix3,
  NearFarScalar,
  Quaternion,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Viewer,
  Resource,
} from "cesium";
import { Scan, ScanRepresentation, Scanset } from "lib/types/Scanset";
import { DeferredValue, Guard } from "lib/actions/util";
import {
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

type ScanState = Scan & {
  contentResource: Resource;
  thumbnailResource: Resource;
  horizon: Horizon | null;
};

// Pannellum's horizonPitch and horizonRoll, in degrees
type Horizon = {
  pitch: number;
  roll: number;
};

type ScansetState = {
  id: string;
  status: "loading" | "ready";
  scans: Array<ScanState> | null;
  billboards: BillboardCollection | null;
  bbox: BBox | null;
};

type PositionEvent = {
  position: Cartesian2;
};

type MotionEvent = {
  startPosition: Cartesian2;
  endPosition: Cartesian2;
};

const representationsCanPreview: ReadonlySet<ScanRepresentation> = new Set([
  "spherical",
  "pinhole",
  "cube",
  "cylindrical",
]);
const representationsCanSelect: ReadonlySet<ScanRepresentation> = new Set([
  "spherical",
]);

function canPreviewRepresentation(
  representation: ScanRepresentation | undefined,
) {
  if (!representation) return false;
  return representationsCanPreview.has(representation);
}

function canSelectRepresentation(
  representation: ScanRepresentation | undefined,
) {
  if (!representation) return false;
  return representationsCanSelect.has(representation);
}

// Works out Pannellum's horizonPitch and horizonRoll from the scan's pose.
// Older scan sets don't have a pose, so they don't get leveled.
function getHorizon(pose: number[] | undefined): Horizon | null {
  if (!pose) return null;

  // The pose is in the scanner's local east-north-up frame, so its bottom
  // row is which way is up, from the scanner's point of view.
  const rotation = Matrix3.fromQuaternion(
    Quaternion.unpack(pose),
    new Matrix3(),
  );
  const up = Matrix3.getRow(rotation, 2, new Cartesian3());
  const sign = up.z < 0 ? -1 : 1;
  // Upside down comes out as roll 180 rather than pitch 180, so the pano
  // still opens facing the same way.
  const pitch = Math.atan2(up.x * sign, Math.abs(up.z));
  const roll = Math.atan2(up.y, sign * Math.hypot(up.x, up.z));
  return {
    pitch: CesiumMath.toDegrees(pitch),
    roll: CesiumMath.toDegrees(roll),
  };
}

export default class ScansetPlugin implements AttachablePluginInterface {
  private readonly _viewer = new DeferredValue<Viewer>();
  private readonly _selectedScan = new ObservableProperty<ScanState | null>(
    null,
  );
  private _state: { [id: string]: ScansetState } = {};
  private _scanPreviewEntity: Entity | null = null;
  private _currentPreviewId: string | null = null;
  private _screenSpaceEventHandler: ScreenSpaceEventHandler;

  get selectedScan(): ReadonlyObservableProperty<ScanState | null> {
    return this._selectedScan;
  }

  constructor() {
    this.onMouseClick = this.onMouseClick.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
  }

  setViewer(viewer: Viewer) {
    this._viewer.set(viewer);

    this._screenSpaceEventHandler = new ScreenSpaceEventHandler(
      viewer.scene.canvas,
    );
    this._screenSpaceEventHandler.setInputAction(
      this.onMouseClick,
      ScreenSpaceEventType.LEFT_CLICK,
    );
    this._screenSpaceEventHandler.setInputAction(
      this.onMouseMove,
      ScreenSpaceEventType.MOUSE_MOVE,
    );
  }

  detachViewer() {
    this.destroy();
    this._viewer.clear();
  }

  get(id: string): ScansetState {
    return this.getState(id) ?? null;
  }

  isExisting(id: string) {
    Guard.require(id, "id");
    return !!this.getState(id);
  }

  isReady(id: string) {
    Guard.require(id, "id");
    return this.getState(id)?.status === "ready";
  }

  isShowing(id: string) {
    Guard.require(id, "id");
    return !!this.getState(id)?.billboards?.show;
  }

  async load(id: string, resource: Resource) {
    Guard.require(id, "id");
    Guard.require(resource, "resource");
    if (this.getState(id)) return;

    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    this.setState(id, {
      id,
      status: "loading",
      scans: null,
      billboards: null,
      bbox: null,
    });
    const { scans, billboards, bbox } = await this.createScanset(
      id,
      resource,
      viewer,
    );
    viewer.scene.primitives.add(billboards);
    this.setState(id, {
      id,
      status: "ready",
      scans,
      billboards,
      bbox,
    });
    viewer.scene.requestRender();
  }

  show(id: string) {
    const state = this.getState(id);
    if (state?.billboards) {
      state.billboards.show = true;
      this._viewer.current?.scene.requestRender();
    }
  }

  hide(id: string) {
    const state = this.getState(id);
    if (state?.billboards) {
      state.billboards.show = false;
      const selectedScanId = this._selectedScan.getSnapshot()?.id;
      if (state.scans.some((s) => s.id === selectedScanId))
        this.clearSelectedScan();
      this._viewer.current?.scene.requestRender();
    }
  }

  clearSelectedScan() {
    if (this._selectedScan.getSnapshot()) this._selectedScan.set(null);
  }

  private getState(id: string) {
    return this._state[id] ?? null;
  }

  private setState(id: string, state: ScansetState) {
    if (state) this._state[id] = state;
    else delete this._state[id];
  }

  private destroy() {
    this._screenSpaceEventHandler?.destroy();
    this._screenSpaceEventHandler = null;

    this.clearSelectedScan();

    const viewer = this._viewer.current;
    if (viewer) this.removePreviewEntity(viewer);

    for (const scanset of Object.values(this._state)) {
      viewer?.scene.primitives.remove(scanset.billboards);
      if (!scanset.billboards.isDestroyed()) scanset.billboards.destroy();
    }
    this._state = {};
  }

  private async createScanset(id: string, resource: Resource, viewer: Viewer) {
    const scanset = (await resource.fetchJson()) as Scanset;

    const billboards = new BillboardCollection({ scene: viewer.scene });
    const scans = scanset.scans.map((scan) => {
      const contentResource = resource.clone();
      contentResource.url = resource.url.replace(
        "scanset.json",
        scan.components[0].fullsize.filename,
      );
      const thumbnailResource = resource.clone();
      thumbnailResource.url = resource.url.replace(
        "scanset.json",
        scan.components[0].thumbnail.filename,
      );
      const position = new Cartesian3(
        scan.location.transform[12],
        scan.location.transform[13],
        scan.location.transform[14],
      );
      billboards.add({
        id: scan,
        position,
        image: "/images/billboards/pin-camera.svg",
        scaleByDistance: new NearFarScalar(100, 1, 1000, 0.1),
      });
      return {
        ...scan,
        contentResource,
        thumbnailResource,
        horizon: getHorizon(scan.location.pose),
      };
    });
    return {
      scans,
      billboards,
      bbox: bbox(
        featureCollection(
          scanset.scans.map((scan) => point(scan.location.wgs84)),
        ),
      ),
    };
  }

  private getScanById(scanId: string) {
    for (const scanset of Object.values(this._state)) {
      const scan = scanset.scans.find((s) => s.id === scanId);
      if (scan) return scan;
    }
    return null;
  }

  private isAnyShowing() {
    for (const scanset of Object.values(this._state)) {
      if (scanset.billboards?.show) return true;
    }
    return false;
  }

  private async createPreviewEntity(scan: ScanState, viewer: Viewer) {
    this.removePreviewEntity(viewer);
    this._currentPreviewId = scan.id;

    const image = new Image();
    image.onload = () => {
      if (
        this._scanPreviewEntity !== null ||
        this._currentPreviewId !== scan.id
      ) {
        return;
      }

      const previewImageCanvas = this.createPreviewImageCanvas(image, scan);
      const offsetY = -(previewImageCanvas.height / 2 + 40);
      const dataUrl = previewImageCanvas.toDataURL();
      this._scanPreviewEntity = viewer.entities.add({
        position: new Cartesian3(
          scan.location.transform[12],
          scan.location.transform[13],
          scan.location.transform[14],
        ),
        billboard: {
          image: dataUrl,
          pixelOffset: new Cartesian2(0, offsetY),
          scale: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: new DistanceDisplayCondition(
            0,
            Number.POSITIVE_INFINITY,
          ),
        },
      });
      //Tricky bit:
      //Without waiting, sometimes the entity arrives after the render.
      setTimeout(() => viewer.scene.requestRender(), 100);
    };
    image.onerror = (error) => {
      console.error("Failed to load scanset thumbnail. Id: ", scan.id, error);
    };

    image.crossOrigin = "anonymous";
    image.src = scan.thumbnailResource.url;
  }

  private removePreviewEntity(viewer: Viewer) {
    this._currentPreviewId = null;
    if (this._scanPreviewEntity) {
      viewer.entities.remove(this._scanPreviewEntity);
      this._scanPreviewEntity = null;
      viewer.scene.requestRender();
    }
  }

  private createPreviewImageCanvas(image: HTMLImageElement, scan: ScanState) {
    const canvas = document.createElement("canvas");
    const padding = 12;
    const borderRadius = 8;
    const textHeight = 25;
    const containerHeight = image.height + padding * 2 + textHeight;
    const containerWidth = image.width + padding * 2;

    canvas.width = containerWidth;
    canvas.height = containerHeight;

    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "white";
    ctx.beginPath();
    ctx.roundRect(0, 0, containerWidth, containerHeight, borderRadius);
    ctx.fill();

    // Flip the thumbnail if the scanner was upside down
    if (scan.horizon && Math.abs(scan.horizon.roll) > 90) {
      ctx.translate(containerWidth, image.height + padding * 2);
      ctx.scale(-1, -1);
    }
    ctx.drawImage(image, padding, padding, image.width, image.height);
    ctx.resetTransform();

    ctx.fillStyle = "#191a1c";
    ctx.font = "14px Inter, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(scan.id, padding, containerHeight - padding);

    return canvas;
  }

  private onMouseMove(movement: MotionEvent) {
    const viewer = this._viewer.current;
    if (!viewer || !this.isAnyShowing()) return;

    const pick = viewer.scene.pick(movement.endPosition) as { id: Scan };
    if (!canPreviewRepresentation(pick?.id?.representation)) {
      this.removePreviewEntity(viewer);

      document.body.style.cursor = "default";
      return;
    }

    const pickId = pick.id.id;
    if (this._currentPreviewId === pickId) return;

    const scan = this.getScanById(pickId);
    this.createPreviewEntity(scan, viewer);

    if (canSelectRepresentation(pick.id.representation)) {
      document.body.style.cursor = "pointer";
    }
  }

  private onMouseClick(movement: PositionEvent) {
    const viewer = this._viewer.current;
    if (!viewer || !this.isAnyShowing()) return;

    const pick = viewer.scene.pick(movement.position) as { id: Scan };
    if (canSelectRepresentation(pick?.id?.representation)) {
      const scan = this.getScanById(pick.id.id);
      this._selectedScan.set(scan);
      this.removePreviewEntity(viewer);
    }
  }
}
